import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { useMarket } from '@/lib/market/market-context';

import { clampAsOf, isPastAsOf, marketToday, parseAsOf } from './asof';

/**
 * =============================================================================
 *  全域 as_of 的唯一讀寫點。
 *
 *  **它住在 URL 的 search param，不是 Zustand store。**
 *  §2.6 路由那一列逐字：「`?symbol`、`?range`、`?indicators`、`?as_of` 是**產品狀態**，
 *  必須可用 URL 定址才能分享。這是 typed search params 不可替代的原因。」
 *
 *  具體差別：把 as_of 放進 store，使用者倒帶到 2015-06-30 之後複製網址給同事，
 *  同事看到的是今天。那不是一個小 bug——時光機的整個價值就是「把那一天的畫面給別人看」。
 *
 *  `retainSearchParams(['as_of'])`（見 routes/__root.tsx）讓它在換頁時不會掉。
 * ========================================================================== */

export interface AsOfState {
  /** 目前的 as_of；undefined ＝ 即時（沒有倒帶） */
  asOf: string | undefined;
  /** 市場當地的今天。所有 staleTime 判斷都用它，不用瀏覽器的今天。 */
  today: string;
  /** 嚴格過去才算倒帶 */
  rewound: boolean;
  /** 送給 API 的值：倒帶時是 asOf，即時時是 undefined（不送這個參數） */
  param: string | undefined;
  setAsOf: (next: string | undefined) => void;
}

export function useAsOf(): AsOfState {
  const navigate = useNavigate();
  const { market } = useMarket();
  const search = useSearch({ strict: false });
  const today = useMemo(() => marketToday(market), [market]);

  const raw = (search as { as_of?: unknown }).as_of;
  const parsed = parseAsOf(raw);
  const asOf = parsed === undefined ? undefined : clampAsOf(parsed, today);

  const setAsOf = useCallback(
    (next: string | undefined) => {
      const value = next === undefined ? undefined : clampAsOf(next, today);
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => {
          const { as_of: _drop, ...rest } = prev;
          return value === undefined ? rest : { ...rest, as_of: value };
        },
        // 倒帶是一個「看一下」的動作，不該把瀏覽器上一頁塞滿十幾個日期。
        replace: true,
      });
    },
    [navigate, today],
  );

  return {
    asOf,
    today,
    rewound: isPastAsOf(asOf, today),
    param: asOf,
    setAsOf,
  };
}
